import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import OCAuth from '../client/OCAuth';
import { useHandleAuthRedirectCallbackMutation } from '../query/currentUserQuery';

const AppUrlListener: React.FC = () => {
  const history = useHistory();
  const { mutateAsync: handleAuthRedirectCallback } = useHandleAuthRedirectCallbackMutation();

  useEffect(() => {
    const handleIncomingUrl = async (rawUrl: string) => {
      if (!rawUrl) {
        return;
      }

      if (OCAuth.isRedirectCallback(rawUrl)) {
        try {
          await handleAuthRedirectCallback(rawUrl);
        } finally {
          await Browser.close().catch(() => undefined);
        }
        return;
      }

      let appPath: string | null = null;
      try {
        const parsed = new URL(rawUrl);
        appPath = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      } catch (error) {
        appPath = null;
      }

      if (!appPath) {
        return;
      }

      if (appPath.startsWith('/payment')) {
        await Browser.close().catch(() => undefined);
        return;
      }

      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (appPath === currentPath) {
        return;
      }

      history.push(appPath);
    };

    // Handle auth redirect callback on initial app/web load.
    if (OCAuth.isRedirectCallback(window.location.href)) {
      void handleIncomingUrl(window.location.href).catch(() => undefined);
    }
    void App.getLaunchUrl()
      .then((launchUrl) => {
        if (launchUrl?.url) {
          return handleIncomingUrl(launchUrl.url);
        }
      })
      .catch(() => undefined);

    const listenerPromise = App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      void handleIncomingUrl(event.url).catch(() => undefined);
    });

    return () => {
      listenerPromise.then(listenerHandle => {
        listenerHandle.remove();
      });
    };
  }, [handleAuthRedirectCallback, history]);

  return null;
};

export default AppUrlListener;
